/**
 * StarterCards — the "start from one of these" cards an empty page offers:
 * one sentence per card, read as a whole, and one button that takes it.
 *
 * Drawn by Automations (example rules) and the Report Designer (example
 * searches), so both pages show starters the same way. A card with no `onUse`
 * draws no button — for a person who may not take it (build contract §2.8).
 */

import type { ReactNode } from 'react';
import { btnSecondary, panel } from '../mono/primitives';

export interface StarterCard {
  key: string;
  /** The starter, said as one plain sentence. */
  sentence: string;
  /** Takes the starter. Left out when the person may not. */
  onUse?: () => void;
}

interface StarterCardsProps {
  /** The section's accessible name, e.g. "Example rules". */
  label: string;
  /** The line above the cards. */
  lead: ReactNode;
  /** The button's words, e.g. "Use this rule". */
  useLabel: string;
  cards: StarterCard[];
  columns?: 2 | 3;
  /** Test id on each card. */
  cardTestId?: string;
  /** Drawn below the cards (e.g. "Start from a blank rule"). */
  children?: ReactNode;
}

export function StarterCards({ label, lead, useLabel, cards, columns = 2, cardTestId, children }: StarterCardsProps) {
  return (
    <section className="space-y-3" aria-label={label}>
      <p className="text-sm text-[var(--text-sec)]">{lead}</p>
      <div className={`grid gap-3 ${columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {cards.map((card) => (
          <div key={card.key} {...panel('flex flex-col justify-between gap-3 p-4')} data-testid={cardTestId}>
            <p className="font-serif text-[1.05rem] leading-snug text-[var(--text-pri)]">{card.sentence}</p>
            {card.onUse && (
              <div>
                <button type="button" {...btnSecondary()} onClick={card.onUse}>
                  {useLabel}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {children}
    </section>
  );
}
